<template>
  <div class="container">
    <!-- 页面头部 -->
    <header class="header">
      <div class="header-left">
        <h1 class="title">组队挂机</h1>
        <!-- 提示信息 -->
        <div class="team-tips">
          <div class="tip-item">
            <span class="tip-icon">📋</span>
            <span class="tip-text">同一个组队的5个号必须用相同的挂机地图和定点挂机位置，频道必须也要相同</span>
          </div>
          <div class="tip-item">
            <span class="tip-icon">👑</span>
            <span class="tip-text">队长：5个队员会争抢队长，先到的先当。队长离线（5分钟）队员会自动申请当队长</span>
          </div>
        </div>
      </div>
      <button class="btn-primary" @click="openCreateDialog">
        <span class="icon">+</span> 创建组队
      </button>
    </header>

    <!-- 创建组队对话框 -->
    <el-dialog
      v-model="showCreateDialog"
      title="创建新组队"
      width="520px"
      @close="resetForm"
      class="create-dialog"
    >
      <form class="form" @submit.prevent="handleCreateTeam">
        <!-- 队伍名称 -->
        <div class="form-group">
          <label class="form-label">队伍名称</label>
          <input
            v-model="form.teamName"
            type="text"
            placeholder="输入队伍名称"
            class="form-input"
            maxlength="50"
          />
        </div>

        <!-- 窗口选择 -->
        <div class="form-group">
          <div class="form-label-row">
            <label class="form-label">选择窗口 ID</label>
            <span class="form-count">{{ form.windowIds.length }}/5</span>
          </div>
          <el-select
            v-model="form.windowIds"
            multiple
            placeholder="选择或输入窗口 ID（2-5 个）"
            filterable
            allow-create
            collapse-tags
            collapse-tags-tooltip
            :max-collapse-tags="2"
            class="form-select"
          >
            <el-option
              v-for="role in availableRoles"
              :key="role.window_id"
              :label="role.window_id || '?'"
              :value="role.window_id"
            >
              <div class="role-option-item">
                <span class="role-option-label">等级:</span>
                <span class="role-option-value">{{ role.level || '?' }}</span>
                <span class="role-option-label">大区:</span>
                <span class="role-option-value">{{ role.region || '未知' }}</span>
                <span class="role-option-label">姓名:</span>
                <span class="role-option-value">{{ role.name || '未知' }}</span>
                <span class="role-option-label">战力:</span>
                <span class="role-option-value">{{ formatCombatPower(role.combat_power) }}</span>
                <span class="role-option-label">窗口:</span>
                <span class="role-option-value">{{ role.window_id || '?' }}</span>
              </div>
            </el-option>
          </el-select>
          <p class="form-hint">支持手动输入新窗口 ID，最多 5 个</p>
        </div>
      </form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showCreateDialog = false">取消</el-button>
          <el-button
            type="primary"
            @click="handleCreateTeam"
            :disabled="!isFormValid"
            :loading="isCreating"
          >
            创建
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 添加窗口对话框 -->
    <el-dialog
      v-model="showAddWindowDialog"
      title="添加窗口到队伍"
      width="520px"
      @close="resetAddWindowForm"
      class="add-window-dialog"
    >
      <form class="form" @submit.prevent="handleAddWindow">
        <div class="form-group">
          <label class="form-label">选择窗口 ID</label>
          <el-select
            v-model="addWindowForm.windowId"
            placeholder="选择窗口 ID"
            filterable
            allow-create
            class="form-select"
          >
            <el-option
              v-for="role in availableWindowsForAdd"
              :key="role.window_id"
              :label="role.window_id || '?'"
              :value="role.window_id"
            >
              <div class="role-option-item">
                <span class="role-option-label">等级:</span>
                <span class="role-option-value">{{ role.level || '?' }}</span>
                <span class="role-option-label">大区:</span>
                <span class="role-option-value">{{ role.region || '未知' }}</span>
                <span class="role-option-label">姓名:</span>
                <span class="role-option-value">{{ role.name || '未知' }}</span>
                <span class="role-option-label">战力:</span>
                <span class="role-option-value">{{ formatCombatPower(role.combat_power) }}</span>
                <span class="role-option-label">窗口:</span>
                <span class="role-option-value">{{ role.window_id || '?' }}</span>
              </div>
            </el-option>
          </el-select>
          <p class="form-hint">选择一个可用的窗口添加到队伍中</p>
        </div>
      </form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showAddWindowDialog = false">取消</el-button>
          <el-button
            type="primary"
            @click="handleAddWindow"
            :disabled="!addWindowForm.windowId"
            :loading="isAddingWindow"
          >
            添加
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 编辑组队对话框 -->
    <el-dialog
      v-model="showEditDialog"
      title="编辑队伍名称"
      width="420px"
      @close="resetEditForm"
      class="edit-dialog"
    >
      <form class="form" @submit.prevent="handleUpdateTeam">
        <div class="form-group">
          <label class="form-label">队伍名称</label>
          <input
            v-model="editForm.teamName"
            type="text"
            placeholder="输入队伍名称"
            class="form-input"
            maxlength="50"
          />
        </div>
      </form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showEditDialog = false">取消</el-button>
          <el-button
            type="primary"
            @click="handleUpdateTeam"
            :disabled="!editForm.teamName.trim()"
            :loading="isUpdating"
          >
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 编辑备用名对话框 -->
    <el-dialog
      v-model="showEditBakNameDialog"
      title="编辑备用名"
      width="420px"
      @close="resetEditBakNameForm"
      class="edit-bak-name-dialog"
    >
      <form class="form" @submit.prevent="handleUpdateBakName">
        <div class="form-group">
          <label class="form-label">窗口ID</label>
          <input
            :value="editBakNameForm.windowId"
            type="text"
            class="form-input"
            disabled
          />
        </div>
        <div class="form-group">
          <label class="form-label">备用名</label>
          <input
            v-model="editBakNameForm.bakName"
            type="text"
            placeholder="输入备用名（留空则使用角色名称）"
            class="form-input"
            maxlength="255"
          />
          <p class="form-hint">留空则显示角色名称</p>
        </div>
        <div class="form-group" style="margin-top: 16px; padding: 12px; background-color: #f0f9ff; border-radius: 4px; border-left: 3px solid #3b82f6;">
          <p style="margin: 0; font-size: 13px; color: #1e40af; line-height: 1.6;">
            <strong>💡 提示：</strong>游戏内OCR有可能名称识别有错误，可以比如大写i和l分不清。所以如果有问题的可以在这里设置备用名称，队长优先使用备用名称进行搜索哦
          </p>
        </div>
      </form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showEditBakNameDialog = false">取消</el-button>
          <el-button
            type="primary"
            @click="handleUpdateBakName"
            :loading="isUpdatingBakName"
          >
            保存
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 内容区域 -->
    <main class="content">
      <!-- 组队列表 -->
      <div v-if="!loading && teams.length > 0" class="teams-list">
        <div 
          v-for="team in teams" 
          :key="team.team_id" 
          class="team-card"
          :class="{ 'team-card-new': newlyCreatedTeamId === team.team_id }"
        >
          <div class="card-content-wrapper">
            <!-- 窗口列表 -->
            <div class="windows-grid">
              <div v-for="windowId in team.window_ids" :key="windowId" class="role-card">
                <button
                  class="btn-remove-window"
                  @click="handleRemoveWindow(team.team_id, windowId)"
                  title="删除此窗口"
                  :disabled="team.window_ids.length <= 2"
                >
                  ✕
                </button>
                <div class="role-header">
                  <div class="role-title">
                    <span 
                      class="role-name" 
                      @click="openEditBakNameDialog(team, windowId)"
                      style="cursor: pointer; text-decoration: underline;"
                      title="点击编辑备用名"
                    >
                      {{ getWindowDisplayName(team, windowId) }}
                    </span>
                    <span v-if="isInTeam(team, windowId)" class="in-team-badge" title="已进组">【已进组】</span>
                    <span class="window-badge">{{ windowId }}</span>
                    <span v-if="isLeader(team, windowId)" class="leader-flag" title="队长">🚩</span>
                  </div>
                  <div class="role-subtitle">
                    <span>{{ getRoleInfo(windowId)?.datas?.class_name || '未知职业' }}</span>
                    <span class="divider">•</span>
                    <span>Lv.{{ getRoleInfo(windowId)?.level || '?' }}</span>
                  </div>
                </div>
                
                <div v-if="getRoleInfo(windowId)" class="role-body">
                  <div class="info-row">
                    <span class="info-label">大区</span>
                    <span class="info-value">{{ getRoleInfo(windowId).region || '未知' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">战力</span>
                    <span class="info-value">{{ formatCombatPower(getRoleInfo(windowId).combat_power) }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label" title="组队心跳：只有角色在野外挂机或激战地挂机时才会发送心跳时间">组队心跳</span>
                    <span class="info-value">
                      {{ formatHeartbeatTime(team, windowId) }}
                      <span v-if="isHeartbeatActive(team, windowId)" class="heartbeat-indicator"></span>
                      <span v-else-if="isHeartbeatOffline(team, windowId)" class="offline-indicator"></span>
                    </span>
                  </div>
                  <div class="divider-line"></div>
                  <div class="currency-row">
                    <div class="currency-item">
                      <span class="currency-icon">💎</span>
                      <span class="currency-value">{{ formatNumber(getRoleInfo(windowId).datas?.diamond) }}</span>
                    </div>
                    <div class="currency-item">
                      <span class="currency-icon">琥珀</span>
                      <span class="currency-value">{{ formatNumber(getRoleInfo(windowId).datas?.amber2 || getRoleInfo(windowId).datas?.amber) }}</span>
                    </div>
                  </div>
                </div>
                
                <div v-else class="role-body empty">
                  <span class="empty-msg">窗口未找到角色信息</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 卡片信息 - 右侧 -->
          <div class="card-header-right">
            <!-- 1. 统一头部 (合并 Team Header 和 Control Panel) -->
            <div class="header-unified">
              <div class="header-main">
                <div class="team-title-row">
                  <h2 class="team-name">{{ team.team_name }}</h2>
                  <button
                    class="btn-icon-edit"
                    @click="openEditDialog(team)"
                    title="编辑队伍名称"
                  >
                    ✏️
                  </button>
                </div>
                <div class="team-subtitle">
                  <span class="team-count">{{ team.window_ids.length }} 窗口</span>
                  <div v-if="hasMixedRegions(team)" class="region-warning">
                    <span class="warning-icon">⚠️</span> 跨区
                  </div>
                </div>
              </div>
              
              <div class="header-controls">
                <div class="enable-switch">
                  <el-switch
                    v-model="team.enabled"
                    :active-value="true"
                    :inactive-value="false"
                    @change="handleStatusChange(team)"
                    inline-prompt
                    active-text="ON"
                    inactive-text="OFF"
                    style="--el-switch-on-color: #13ce66; --el-switch-off-color: #ff4949"
                  />
                </div>
                <div class="status-mini-grid">
                  <span class="status-badge" :class="team.status === '组队中' ? 'status-active' : ''">{{ team.status || '待组队' }}</span>
                  <span class="map-badge" :title="team.current_map">{{ team.current_map || '无地图' }}</span>
                </div>
              </div>
            </div>

            <!-- 2. 配置面板 -->
            <div class="config-panel">
              <div class="config-row">
                <label class="config-label" title="开启后，需要提前5个号加好友">好友组队</label>
                <el-switch
                  v-model="team.friend_team_enabled"
                  @change="(val) => handleConfigChange(team, 'friend_team_enabled', val)"
                  size="small"
                />
              </div>
              <div class="config-row">
                <label class="config-label" title="开启后，会招募附近队员">招募附近</label>
                <el-switch
                  v-model="team.follow_leader_enabled"
                  @change="(val) => handleConfigChange(team, 'follow_leader_enabled', val)"
                  size="small"
                />
              </div>
              <div class="config-row">
                <label class="config-label" title="开启后，5个队员凑齐后会自动进行远征">组队远征</label>
                <el-switch
                  v-model="team.expedition_enabled"
                  @change="(val) => handleConfigChange(team, 'expedition_enabled', val)"
                  size="small"
                />
              </div>
              <div class="config-row slider-row">
                <label class="config-label" title="识别队长预值(0~1)">识别预值</label>
                <div class="slider-container">
                  <el-slider
                    v-model="team.leader_recognition_threshold"
                    :min="0"
                    :max="1"
                    :step="0.1"
                    size="small"
                    :show-tooltip="false"
                    @change="(val) => handleConfigChange(team, 'leader_recognition_threshold', val)"
                  />
                  <span class="slider-value">{{ team.leader_recognition_threshold || 0.8 }}</span>
                </div>
              </div>
            </div>
            
            <!-- 4. 底部操作 -->
            <div class="team-actions">
              <button
                class="btn-action btn-add"
                @click="openAddWindowDialog(team)"
                title="添加窗口"
                :disabled="team.window_ids.length >= 5"
              >
                + 添加窗口
              </button>
              <button
                class="btn-action btn-del"
                @click="handleDeleteTeam(team.team_id)"
                title="删除组队"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="!loading && teams.length === 0" class="empty-state">
        <p class="empty-text">暂无组队</p>
        <p class="empty-hint">点击"创建组队"按钮开始建立你的第一个组队</p>
      </div>

      <!-- 加载状态 -->
      <div v-else class="loading-state">
        <el-skeleton :rows="3" animated />
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getDevicesInfo } from '../api/roles'
import { createTeam, getUserTeams, deleteTeam, updateTeamName, addWindowToTeam, removeWindowFromTeam, updateTeamStatus, updateWindowBakName, updateTeamConfig } from '../api/teams'

// 状态
const loading = ref(false)
const isCreating = ref(false)
const showCreateDialog = ref(false)
const showEditDialog = ref(false)
const showAddWindowDialog = ref(false)
const showEditBakNameDialog = ref(false)
const teams = ref([])
const roles = ref([])
const form = ref({ teamName: '', windowIds: [] })
const editForm = ref({ teamName: '', teamId: '' })
const addWindowForm = ref({ teamId: '', windowId: '' })
const editBakNameForm = ref({ teamId: '', windowId: '', bakName: '' })
const isUpdating = ref(false)
const isAddingWindow = ref(false)
const isRemovingWindow = ref(false)
const isUpdatingBakName = ref(false)
const newlyCreatedTeamId = ref(null) // 记录刚创建的组队ID

// 计算属性
// 过滤掉已经加入其他队伍的窗口ID
const availableRoles = computed(() => {
  // 收集所有已加入队伍的窗口ID
  const usedWindowIds = new Set()
  teams.value.forEach(team => {
    if (team.window_ids && Array.isArray(team.window_ids)) {
      team.window_ids.forEach(windowId => {
        usedWindowIds.add(windowId)
      })
    }
  })
  
  // 过滤掉已使用的窗口ID
  return roles.value.filter(role => !usedWindowIds.has(role.window_id))
})

// 获取可添加到当前队伍的窗口（排除该队伍已有的窗口）
const availableWindowsForAdd = computed(() => {
  if (!addWindowForm.value.teamId) {
    return availableRoles.value
  }
  
  // 找到当前队伍
  const currentTeam = teams.value.find(t => t.team_id === addWindowForm.value.teamId)
  if (!currentTeam) {
    return availableRoles.value
  }
  
  // 排除当前队伍已有的窗口
  const currentTeamWindowIds = new Set(currentTeam.window_ids || [])
  return availableRoles.value.filter(role => !currentTeamWindowIds.has(role.window_id))
})

const isFormValid = computed(() => {
  const count = form.value.windowIds.length
  return form.value.teamName.trim().length > 0 && count >= 2 && count <= 5
})

// 生成默认队伍名称
const getDefaultTeamName = () => {
  const count = teams.value.length
  return `队伍${count + 1}`
}

// 获取角色信息
const getRoleInfo = (windowId) => roles.value.find(r => r.window_id === windowId)

// 获取窗口的备用名
const getWindowBakName = (team, windowId) => {
  if (!team.window_details || !Array.isArray(team.window_details)) {
    return null
  }
  const windowDetail = team.window_details.find(w => w.window_id === windowId)
  return windowDetail?.bak_name || null
}

// 获取窗口的显示名称（优先显示备用名，否则显示角色名称）
const getWindowDisplayName = (team, windowId) => {
  const bakName = getWindowBakName(team, windowId)
  if (bakName && bakName.trim()) {
    return bakName
  }
  return getRoleInfo(windowId)?.name || '未知角色'
}

// 判断是否是队长
const isLeader = (team, windowId) => {
  if (!team.window_details || !Array.isArray(team.window_details)) {
    return false
  }
  const windowDetail = team.window_details.find(w => w.window_id === windowId)
  return windowDetail && windowDetail.is_leader === true
}

// 判断是否已进组
const isInTeam = (team, windowId) => {
  if (!team.window_details || !Array.isArray(team.window_details)) {
    return false
  }
  const windowDetail = team.window_details.find(w => w.window_id === windowId)
  return windowDetail && windowDetail.in_team === true
}

// 格式化心跳时间
const formatHeartbeatTime = (team, windowId) => {
  if (!team.window_details || !Array.isArray(team.window_details)) {
    return '未知'
  }
  const windowDetail = team.window_details.find(w => w.window_id === windowId)
  if (!windowDetail || !windowDetail.heartbeat_time) {
    return '未知'
  }
  
  const now = new Date()
  const heartbeat = new Date(windowDetail.heartbeat_time)
  const diffMs = now - heartbeat
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  
  if (diffSec < 60) {
    return `${diffSec}秒前`
  } else if (diffMin < 60) {
    return `${diffMin}分钟前`
  } else if (diffHour < 24) {
    return `${diffHour}小时前`
  } else {
    const diffDay = Math.floor(diffHour / 24)
    return `${diffDay}天前`
  }
}

// 判断心跳是否在5分钟内（显示绿色呼吸灯）
const isHeartbeatActive = (team, windowId) => {
  if (!team.window_details || !Array.isArray(team.window_details)) {
    return false
  }
  const windowDetail = team.window_details.find(w => w.window_id === windowId)
  if (!windowDetail || !windowDetail.heartbeat_time) {
    return false
  }
  
  const now = new Date()
  const heartbeat = new Date(windowDetail.heartbeat_time)
  const diffMs = now - heartbeat
  const diffMin = Math.floor(diffMs / 1000 / 60)
  
  // 心跳在5分钟内显示绿色呼吸灯
  return diffMin < 5
}

// 判断窗口是否离线（心跳超过5分钟或没有心跳，显示红色点）
const isHeartbeatOffline = (team, windowId) => {
  if (!team.window_details || !Array.isArray(team.window_details)) {
    return true // 没有心跳数据，视为离线
  }
  const windowDetail = team.window_details.find(w => w.window_id === windowId)
  if (!windowDetail || !windowDetail.heartbeat_time) {
    return true // 没有心跳时间，视为离线
  }
  
  const now = new Date()
  const heartbeat = new Date(windowDetail.heartbeat_time)
  const diffMs = now - heartbeat
  const diffMin = Math.floor(diffMs / 1000 / 60)
  
  // 心跳超过5分钟视为离线
  return diffMin >= 5
}

// 处理状态变更
const handleStatusChange = async (team) => {
  try {
    await updateTeamStatus(
      team.team_id,
      team.enabled,
      null, // 队伍状态由API设置，不通过前端修改
      null // 挂机地图由API设置，不通过前端修改
    )
    ElMessage.success('状态更新成功')
  } catch (error) {
    console.error('[TeamManagement] 更新状态失败:', error)
    ElMessage.error(error.response?.data?.message || '更新状态失败，请重试')
    // 重新加载数据以恢复原状态
    await loadTeams()
  }
}

// 检查组队是否包含不同大区
const hasMixedRegions = (team) => {
  if (!team.window_ids || team.window_ids.length === 0) return false
  const regions = team.window_ids
    .map(windowId => {
      const role = getRoleInfo(windowId)
      return role?.region
    })
    .filter(Boolean)
  
  if (regions.length === 0) return false
  
  // 提取每个大区名称中 - 后面的部分进行比较
  // 例如：20-ASIA041 和 19-ASIA041 的 - 后面都是 ASIA041，认为是同区
  const regionSuffixes = regions.map(region => {
    const dashIndex = region.indexOf('-')
    if (dashIndex !== -1 && dashIndex < region.length - 1) {
      // 如果包含 - 且 - 后面有内容，提取 - 后面的部分
      return region.substring(dashIndex + 1)
    }
    // 如果没有 - 或 - 后面没有内容，使用整个大区名称
    return region
  })
  
  const uniqueRegionSuffixes = new Set(regionSuffixes)
  return uniqueRegionSuffixes.size > 1
}

// 格式化战斗力
const formatCombatPower = (power) => {
  if (!power) return '0'
  if (power >= 10000) {
    return (power / 10000).toFixed(1) + '万'
  }
  return power.toString()
}

// 格式化角色标签（用于下拉选择显示）
const formatRoleLabel = (role) => {
  const level = role.level || '?'
  const region = role.region || '未知'
  const name = role.name || '未知'
  const combatPower = formatCombatPower(role.combat_power)
  const windowId = role.window_id || '?'
  return `${level} - ${region} - ${name} - ${combatPower} - ${windowId}`
}

// 格式化钻石
const formatDiamond = (diamond) => {
  if (!diamond) return '0'
  return diamond.toString()
}

// 格式化琥珀
const formatAmber = (amber) => {
  if (!amber) return '0'
  return amber.toString()
}

// 通用数字格式化
const formatNumber = (num) => {
  if (!num || num === 0) return '0'
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + '万'
  }
  return num.toLocaleString()
}

const validateTeam = () => {
  if (!form.value.teamName.trim()) {
    ElMessage.warning('请输入队伍名称')
    return false
  }

  const count = form.value.windowIds.length
  if (count < 2 || count > 5) {
    ElMessage.warning('请选择 2-5 个窗口')
    return false
  }

  return true
}

// 加载角色数据
const loadRoles = async () => {
  try {
    const { data } = await getDevicesInfo({
      page: 1,
      size: 1000,
      sort_by: 'window_id',
      sort_order: 'asc'
    })
    roles.value = data?.items || data || []
  } catch (error) {
    console.error('[TeamManagement] 加载角色失败:', error)
    ElMessage.error('加载角色数据失败，请刷新重试')
  }
}

// 加载组队列表
const loadTeams = async () => {
  loading.value = true
  try {
    const { data } = await getUserTeams()
    const teamsList = data?.teams || []
    // 确保每个team都有必要的字段
    teams.value = teamsList.map(team => ({
      ...team,
      enabled: team.enabled !== undefined ? team.enabled : true,
      status: team.status || '待组队',
      current_map: team.current_map || '',
      window_details: team.window_details || [],
      leader_hang_info: team.leader_hang_info || null,
      friend_team_enabled: team.friend_team_enabled !== undefined ? team.friend_team_enabled : false,
      follow_leader_enabled: team.follow_leader_enabled !== undefined ? team.follow_leader_enabled : false,
      expedition_enabled: team.expedition_enabled !== undefined ? team.expedition_enabled : false,
      leader_recognition_threshold: team.leader_recognition_threshold !== undefined ? team.leader_recognition_threshold : 0.8
    }))
  } catch (error) {
    console.error('[TeamManagement] 加载组队失败:', error)
    ElMessage.error('加载组队列表失败，请刷新重试')
  } finally {
    loading.value = false
  }
}

// 重置表单
const resetForm = () => {
  form.value = { 
    teamName: getDefaultTeamName(), 
    windowIds: [] 
  }
}

// 打开创建对话框
const openCreateDialog = () => {
  resetForm()
  showCreateDialog.value = true
}

// 创建组队
const handleCreateTeam = async () => {
  if (!validateTeam()) return

  isCreating.value = true
  try {
    const response = await createTeam(form.value.teamName, form.value.windowIds)
    ElMessage.success('组队创建成功')
    showCreateDialog.value = false
    resetForm()
    
    // 先保存可能返回的team_id
    let newTeamId = response?.data?.team?.team_id || response?.data?.team_id
    
    await loadTeams()
    
    // 如果没有从响应中获取到team_id，则使用最新的组队（列表中的第一个）
    if (!newTeamId && teams.value.length > 0) {
      newTeamId = teams.value[0].team_id
    }
    
    // 记录新创建的组队ID，用于显示提醒
    if (newTeamId) {
      newlyCreatedTeamId.value = newTeamId
      // 3秒后清除提醒
      setTimeout(() => {
        newlyCreatedTeamId.value = null
      }, 3000)
    }
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '创建失败，请重试')
  } finally {
    isCreating.value = false
  }
}

// 打开编辑对话框
const openEditDialog = (team) => {
  editForm.value = {
    teamName: team.team_name,
    teamId: team.team_id
  }
  showEditDialog.value = true
}

// 重置编辑表单
const resetEditForm = () => {
  editForm.value = { teamName: '', teamId: '' }
}

// 更新组队名称
const handleUpdateTeam = async () => {
  if (!editForm.value.teamName.trim()) {
    ElMessage.warning('请输入队伍名称')
    return
  }

  isUpdating.value = true
  try {
    await updateTeamName(editForm.value.teamId, editForm.value.teamName.trim())
    ElMessage.success('队伍名称更新成功')
    showEditDialog.value = false
    resetEditForm()
    await loadTeams()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '更新失败，请重试')
  } finally {
    isUpdating.value = false
  }
}

// 打开添加窗口对话框
const openAddWindowDialog = (team) => {
  addWindowForm.value = {
    teamId: team.team_id,
    windowId: ''
  }
  showAddWindowDialog.value = true
}

// 重置添加窗口表单
const resetAddWindowForm = () => {
  addWindowForm.value = { teamId: '', windowId: '' }
}

// 添加窗口到队伍
const handleAddWindow = async () => {
  if (!addWindowForm.value.windowId) {
    ElMessage.warning('请选择窗口')
    return
  }

  isAddingWindow.value = true
  try {
    await addWindowToTeam(addWindowForm.value.teamId, addWindowForm.value.windowId)
    ElMessage.success('窗口添加成功')
    showAddWindowDialog.value = false
    resetAddWindowForm()
    await loadTeams()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '添加失败，请重试')
  } finally {
    isAddingWindow.value = false
  }
}

// 打开编辑备用名对话框
const openEditBakNameDialog = (team, windowId) => {
  const currentBakName = getWindowBakName(team, windowId)
  editBakNameForm.value = {
    teamId: team.team_id,
    windowId: windowId,
    bakName: currentBakName || ''
  }
  showEditBakNameDialog.value = true
}

// 重置编辑备用名表单
const resetEditBakNameForm = () => {
  editBakNameForm.value = { teamId: '', windowId: '', bakName: '' }
}

// 更新备用名
const handleUpdateBakName = async () => {
  isUpdatingBakName.value = true
  try {
    await updateWindowBakName(
      editBakNameForm.value.teamId,
      editBakNameForm.value.windowId,
      editBakNameForm.value.bakName || null
    )
    ElMessage.success('备用名更新成功')
    showEditBakNameDialog.value = false
    resetEditBakNameForm()
    await loadTeams()
  } catch (error) {
    ElMessage.error(error.response?.data?.message || '更新失败，请重试')
  } finally {
    isUpdatingBakName.value = false
  }
}

// 处理配置变更（直接更新）
const handleConfigChange = async (team, field, value) => {
  // 保存旧值用于回滚
  const oldValue = team[field]
  // 乐观更新
  team[field] = value

  try {
    await updateTeamConfig(team.team_id, {
      friend_team_enabled: team.friend_team_enabled,
      follow_leader_enabled: team.follow_leader_enabled,
      expedition_enabled: team.expedition_enabled,
      leader_recognition_threshold: team.leader_recognition_threshold,
      // 保持后端需要的字段
      leader_hang_info: team.leader_hang_info || null
    })
    ElMessage.success('设置已更新')
  } catch (error) {
    // 失败回滚
    team[field] = oldValue
    console.error('[TeamManagement] 更新配置失败:', error)
    ElMessage.error('设置更新失败，请重试')
  }
}

// 从队伍中删除窗口
const handleRemoveWindow = async (teamId, windowId) => {
  try {
    const team = teams.value.find(t => t.team_id === teamId)
    if (team && team.window_ids.length <= 2) {
      ElMessage.warning('队伍至少需要2个窗口，无法删除')
      return
    }

    await ElMessageBox.confirm(
      `确定要从队伍中删除窗口 ${windowId} 吗？`,
      '删除确认',
      {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )
    
    isRemovingWindow.value = true
    await removeWindowFromTeam(teamId, windowId)
    ElMessage.success('窗口已删除')
    await loadTeams()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || '删除失败，请重试')
    }
  } finally {
    isRemovingWindow.value = false
  }
}

// 删除组队
const handleDeleteTeam = async (teamId) => {
  try {
    await ElMessageBox.confirm('确定要删除此组队吗？此操作不可恢复', '删除确认', {
      confirmButtonText: '确定删除',
      cancelButtonText: '取消',
      type: 'warning'
    })
    await deleteTeam(teamId)
    ElMessage.success('组队已删除')
    await loadTeams()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.response?.data?.message || '删除失败，请重试')
    }
  }
}

// 监听窗口数量变化
watch(() => form.value.windowIds, (newVal) => {
  if (newVal.length > 5) {
    form.value.windowIds = newVal.slice(0, 5)
    ElMessage.warning('最多只能选择 5 个窗口')
  }
})

// 初始化
onMounted(async () => {
  await loadRoles()
  await loadTeams()
  // 初始化表单默认名称
  form.value.teamName = getDefaultTeamName()
})
</script>

<style scoped>
:root {
  --primary: #409eff;
  --primary-hover: #66b1ff;
  --danger: #f56c6c;
  --success: #67c23a;
  --text-primary: #303133;
  --text-secondary: #606266;
  --text-tertiary: #909399;
  --border-light: #e4e7ed;
  --bg-light: #f5f7fa;
  --bg-lighter: #fafafa;
}

* {
  box-sizing: border-box;
}

.container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 16px 16px 32px;
  box-sizing: border-box;
  background-color: var(--bg-lighter);
}

/* 头部样式 */
.header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
}

.title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: #1e293b;
  letter-spacing: -0.5px;
}

/* 提示信息样式 */
.team-tips {
  margin: 0;
  padding: 6px 10px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8f4f8 100%);
  border: 1px solid #d0e7f0;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex-shrink: 1;
  min-width: 0;
  max-width: 600px;
}

.tip-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tip-icon {
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 1px;
}

.tip-text {
  font-size: 12px;
  color: #606266;
  line-height: 1.5;
  flex: 1;
}

/* 按钮样式 */
.btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  background-color: #409eff;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(64, 158, 255, 0.2);
}

.btn-primary:hover {
  background-color: #66b1ff;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(64, 158, 255, 0.3);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(64, 158, 255, 0.2);
}

.btn-primary:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.2);
}

.icon {
  font-size: 16px;
  font-weight: bold;
}

/* 对话框样式 */
.create-dialog :deep(.el-dialog__header) {
  border-bottom: 1px solid var(--border-light);
  padding: 16px 20px;
}

.create-dialog :deep(.el-dialog__body) {
  padding: 24px 20px;
}

.form {
  padding: 0;
}

.form-group {
  margin-bottom: 24px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.form-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.form-count {
  font-size: 12px;
  color: var(--text-tertiary);
  background-color: var(--bg-light);
  padding: 2px 8px;
  border-radius: 4px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  font-size: 14px;
  color: var(--text-primary);
  border: 1px solid var(--border-light);
  border-radius: 6px;
  background-color: white;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

/* 角色选项样式 - 工整对齐显示 */
.role-option-item {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto 1fr auto 1fr auto 1fr;
  gap: 4px 8px;
  align-items: center;
  padding: 6px 0;
  line-height: 1.5;
}

.role-option-label {
  color: #909399;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.role-option-value {
  color: #303133;
  font-size: 13px;
  font-weight: 400;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-select {
  width: 100%;
}

/* 下拉选项悬停和选中状态样式 */
:deep(.el-select-dropdown__item) {
  transition: all 0.2s ease;
}

:deep(.el-select-dropdown__item:hover) {
  background-color: #ecf5ff !important;
  color: #409eff !important;
}

:deep(.el-select-dropdown__item.is-selected) {
  background-color: #409eff !important;
  color: #ffffff !important;
  font-weight: 500;
}

:deep(.el-select-dropdown__item.is-selected:hover) {
  background-color: #66b1ff !important;
  color: #ffffff !important;
}

/* 选中状态下，选项内容的文字颜色也要变白 */
:deep(.el-select-dropdown__item.is-selected .role-option-label) {
  color: rgba(255, 255, 255, 0.8) !important;
}

:deep(.el-select-dropdown__item.is-selected .role-option-value) {
  color: #ffffff !important;
  font-weight: 500;
}

/* 悬停状态下，选项内容的文字颜色也要变化 */
:deep(.el-select-dropdown__item:hover .role-option-label) {
  color: #66b1ff !important;
}

:deep(.el-select-dropdown__item:hover .role-option-value) {
  color: #409eff !important;
}

.form-hint {
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-tertiary);
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.dialog-footer :deep(.el-button) {
  padding: 8px 24px;
}

/* 内容区域 */
.content {
  min-height: 300px;
}

.teams-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.team-card {
  background: white;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  padding: 12px;
  transition: all 0.3s ease;
  display: flex;
  gap: 12px;
}

.team-card:hover {
  border-color: var(--primary);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.team-card-new {
  background: #fff5f5;
  border-color: #feb2b2;
  box-shadow: 0 0 0 3px rgba(254, 178, 178, 0.2);
}

.card-content-wrapper {
  flex: 1;
}

.card-header-right {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 16px;
  border-left: 1px solid var(--border-light);
  width: 320px;
  min-width: 320px;
  flex-shrink: 0;
}

/* 1. 统一头部 */
.header-unified {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 0;
}

.header-main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.team-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.team-name {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.btn-icon-edit {
  border: none;
  background: none;
  cursor: pointer;
  font-size: 14px;
  padding: 2px;
  color: var(--text-tertiary);
  transition: color 0.2s;
}

.btn-icon-edit:hover {
  color: var(--primary);
}

.team-subtitle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  line-height: 1.2;
}

.team-count {
  color: var(--text-tertiary);
  white-space: nowrap;
}

.region-warning {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 1px 4px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 3px;
  color: #c53030;
  font-size: 10px;
  white-space: nowrap;
}

.header-controls {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
}

.status-mini-grid {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.status-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  background: #f1f5f9;
  color: #64748b;
  white-space: nowrap;
  line-height: 1.3;
}

.status-active {
  background: #ecfdf5;
  color: #059669;
}

.map-badge {
  font-size: 10px;
  color: #94a3b8;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
}

/* 2. 配置面板 (调整间距) */
.config-panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 12px;
  padding: 6px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.config-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 20px;
}

.config-label {
  font-size: 11px;
  color: #475569;
  cursor: help;
  line-height: 1.2;
}

.slider-row {
  grid-column: span 2;
  margin-top: 2px;
}

.slider-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 12px;
  max-width: 200px;
}

.slider-value {
  font-size: 12px;
  color: #475569;
  width: 24px;
  text-align: right;
}

/* 4. 底部操作 */
.team-actions {
  margin-top: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  width: 100%;
}

.btn-action {
  padding: 4px 0;
  border: none;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-add {
  background: #f0f9ff;
  color: #0ea5e9;
  border: 1px solid #bae6fd;
}

.btn-add:hover:not(:disabled) {
  background: #e0f2fe;
  border-color: #7dd3fc;
}

.btn-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(1);
}

.btn-del {
  background: #fff1f2;
  color: #e11d48;
  border: 1px solid #fecdd3;
}

.btn-del:hover {
  background: #ffe4e6;
  border-color: #fda4af;
}

/* 调整响应式布局 */
@media (max-width: 768px) {
  .card-header-right {
    width: 100%;
    border-left: none;
    border-top: 1px solid var(--border-light);
    padding-left: 0;
    padding-top: 16px;
  }
  
  .config-panel {
    background: #f8fafc;
    padding: 12px;
    border-radius: 8px;
  }
  
  .slider-container {
    max-width: none;
  }
}
.role-card {
  position: relative;
}

.btn-remove-window {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background-color: rgba(245, 101, 101, 0.9);
  color: white;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  z-index: 10;
  opacity: 0;
}

.role-card:hover .btn-remove-window {
  opacity: 1;
}

.btn-remove-window:hover:not(:disabled) {
  background-color: #f56565;
  transform: scale(1.1);
}

.btn-remove-window:disabled {
  background-color: #c0c4cc;
  cursor: not-allowed;
  opacity: 0.5;
}

/* 跨区提醒标签 */
.region-warning {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 1px 4px;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 3px;
  color: #c53030;
  font-size: 10px;
}

.warning-icon {
  font-size: 10px;
}

.warning-text {
  font-weight: 400;
  font-size: 10px;
}

/* 挂机地图显示 */
.current-map-value {
  font-size: 12px;
  color: #606266;
  padding: 4px 8px;
  background: #f5f7fa;
  border-radius: 4px;
  min-width: 100px;
  display: inline-block;
  text-align: left;
}

/* 队伍状态显示 */
.status-value {
  font-size: 12px;
  color: #606266;
  padding: 4px 8px;
  background: #f5f7fa;
  border-radius: 4px;
  min-width: 100px;
  display: inline-block;
  text-align: left;
}

/* 窗口网格 */
.windows-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

/* 角色卡片 */
.role-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.role-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #d0d0d0;
}

/* 卡片头部 */
.role-header {
  padding: 10px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}

.role-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.role-name {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.window-badge {
  font-size: 10px;
  font-weight: 600;
  color: #666;
  background: white;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
  margin-left: 6px;
  flex-shrink: 0;
}

.role-subtitle {
  font-size: 11px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;
}

.divider {
  color: #d0d0d0;
}

/* 卡片主体 */
.role-body {
  padding: 8px 10px;
}

.role-body.empty {
  padding: 16px 12px;
  text-align: center;
}

.empty-msg {
  font-size: 11px;
  color: #999;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 0;
}

.info-label {
  font-size: 11px;
  color: #888;
}

.info-value {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* 绿色呼吸灯效果 */
.heartbeat-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #67c23a;
  border-radius: 50%;
  animation: heartbeat-pulse 2s ease-in-out infinite;
  box-shadow: 0 0 4px rgba(103, 194, 58, 0.6);
}

@keyframes heartbeat-pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.4;
    transform: scale(0.8);
  }
}

/* 红色离线指示器 */
.offline-indicator {
  display: inline-block;
  width: 8px;
  height: 8px;
  background-color: #f56c6c;
  border-radius: 50%;
  box-shadow: 0 0 4px rgba(245, 108, 108, 0.6);
}

.divider-line {
  height: 1px;
  background: #f0f0f0;
  margin: 4px 0;
}

/* 货币行 */
.currency-row {
  display: flex;
  gap: 10px;
  padding-top: 0;
}

.currency-item {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  background: #fafafa;
  border-radius: 6px;
}

.currency-icon {
  font-size: 12px;
}

.currency-value {
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 80px 20px;
}

.empty-text {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px 0;
}

.empty-hint {
  font-size: 14px;
  color: var(--text-tertiary);
  margin: 0;
}

/* 加载状态 */
.loading-state {
  padding: 20px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .container {
    padding: 16px 12px;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
    margin-bottom: 24px;
    gap: 12px;
  }

  .title {
    font-size: 24px;
  }

  .btn-primary {
    width: 100%;
    justify-content: center;
  }

  .team-card {
    flex-direction: column;
  }

  .card-header-right {
    padding-left: 0;
    padding-top: 16px;
    border-left: none;
    border-top: 1px solid var(--border-light);
    min-width: auto;
    width: 100%;
    align-items: flex-start;
  }

  .team-info {
    align-items: center;
  }

  .team-name,
  .team-meta {
    text-align: center;
  }

  .team-actions {
    width: 100%;
    flex-direction: column;
  }

  .team-actions .btn-edit,
  .team-actions .btn-add-window,
  .team-actions .btn-delete {
    width: 100%;
  }

  .windows-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .role-card {
    border-radius: 8px;
  }

  .role-header {
    padding: 8px 10px;
  }

  .role-body {
    padding: 8px 10px;
  }
  
  .currency-row {
    gap: 8px;
  }
  
  .currency-item {
    padding: 5px 8px;
  }
}
</style>
